import {
  getEnvironmentNameFromHostname,
  getEnvironmentSuffix
} from "parog-web/utils/runtime-environment";
import ENV from "parog-web/config/environment";
import type { RumInitConfiguration } from "@datadog/browser-rum";

const MASKED_VALUE = "masked-value";

/** Utility function to mask all query params of a URL string */
function maskQueryParams(urlString: string): string {
  // Build the URL object from urlString
  const url = new URL(urlString);

  // Iterate over each query parameter and replace its value with "masked-value"
  url.searchParams.forEach((_value, key) =>
    url.searchParams.set(key, MASKED_VALUE)
  );

  return url.toString();
}

/** Callback function used as DD init "beforeSend" hook
 * Its goal is to make all the query params of a URL logged by DD to avoid RGPD info leaks
 *
 * Here's the doc page for event types
 * https://docs.datadoghq.com/fr/real_user_monitoring/browser/data_collected#section-overview
 */
const maskEventQueryParams = event => {
  if (event.resource?.url)
    event.resource.url = maskQueryParams(event.resource.url);

  if (event.view?.url) event.view.url = maskQueryParams(event.view.url);
};

export async function initialize(): Promise<void> {
  const clientToken = ENV.DD_RUM_CLIENT_TOKEN;
  const applicationId = ENV.DD_RUM_APPLICATION_ID;

  // We need both clientToken and applicationId!
  if (!clientToken || !applicationId) return;

  const env = getEnvironmentNameFromHostname(window.location.hostname);

  const initConfig: RumInitConfiguration = {
    clientToken,
    applicationId,
    site: "datadoghq.eu",
    service: "wise",
    env,
    version: `${ENV.APP["version"]}`,

    /*
    Sessions will be retained based on the retention filters setup in the RUM interface
    See https://app.datadoghq.eu/rum/application/f53e0bcb-45d3-4069-ba9a-9380f6bef839/retention-filters
    */
    sessionSampleRate: 100,

    sessionReplaySampleRate: 0,
    trackResources: true,
    trackLongTasks: true,
    trackUserInteractions: true,

    // See https://docs.datadoghq.com/fr/data_security/real_user_monitoring/#mask-action-names
    enablePrivacyForActionName: true,

    defaultPrivacyLevel: "mask",
    storeContextsAcrossPages: true,
    beforeSend: event => {
      // Make sure the query params of the URLs contained in the event are masked before sending them to DD
      maskEventQueryParams(event);

      /*
      Reject view events with a referrer that contains something that looks like a sensitive token

      For example, if you are on a page http://wise.semeia.io?foo=bar and reload the page with cmd+R
      The next view event sent to DD would contain "http://wise.semeia.io?foo=bar" as the referrer

      Likewise, this would happen if you navigate from the URL http://wise.semeia.io?foo=bar.
      Once again, the navigation view event sent to DD would contain "http://wise.semeia.io?foo=bar" as the referrer

      We do not want to drop all view events with a referrer though, we would loose too much data,
      so we choose to only drop view events with a referrer containing "token="
      */
      if (event.view?.referrer?.includes("token=")) return false;

      return true;
    }
  };

  // For the "local" env, we do not want to use a RUM proxy
  if (env !== "local")
    initConfig.proxy = options =>
      `https://wise${getEnvironmentSuffix()}.semeia.io${options.path}?${options.parameters}`;

  // Whitelist envs with RUM and early return if the env is not valid for RUM
  const validEnvironments = [
    "local",
    "master",
    "release",
    "preproduction",
    "production"
  ];
  if (!validEnvironments.includes(env)) return;

  // If we reach this point, we are in a valid RUM environment. Let's set it up
  const { datadogRum } = await import("@datadog/browser-rum");
  datadogRum.init(initConfig);
}

export default {
  initialize
};
